import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../contexts/ApiContext';
import { useTheme } from '../../contexts/ThemeContext';
import { IconSun, IconMoon } from '../../components/icons';
import { renderMarkdown } from './ChatThread';
import './ChatShare.css';
import { fmtDateTime } from '../../utils/ruDates';

// Страница не должна попасть в поиск ни при каких условиях: заголовок
// X-Robots-Tag ставит nginx, а мета-теги — страховка на случай, если
// страницу отдаст что-то в обход него.
function usePrivatePageMeta(title) {
    useEffect(() => {
        const managed = [];
        const setMeta = (name, content) => {
            let el = document.head.querySelector(`meta[name="${name}"]`);
            const previous = el ? el.getAttribute('content') : null;
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
            managed.push({ el, previous });
        };
        setMeta('robots', 'noindex, nofollow, noarchive, nosnippet');
        setMeta('googlebot', 'noindex, nofollow');
        setMeta('referrer', 'no-referrer');
        const prevTitle = document.title;
        document.title = title ? `${title} — Энилекс AI` : 'Чат Энилекс AI';
        return () => {
            document.title = prevTitle;
            managed.forEach(({ el, previous }) => {
                if (previous === null) el.remove();
                else el.setAttribute('content', previous);
            });
        };
    }, [title]);
}

function formatDateTime(iso) {
    if (!iso) return '';
    return fmtDateTime(iso);
}

function PublicMessage({ message, authorName }) {
    const isAssistant = message.role === 'assistant';
    const content = useMemo(() => renderMarkdown(message.content || ''), [message.content]);
    return (
        <div className={`chat-message chat-message--${isAssistant ? 'assistant' : 'own'}`}>
            <div className="chat-message__meta">
                <span className="chat-message__author">{isAssistant ? 'AI' : authorName}</span>
                <span className="chat-message__time">{formatDateTime(message.created_at)}</span>
            </div>
            <div className="chat-message__bubble">
                <div className="chat-message__text">{content}</div>
            </div>
        </div>
    );
}

function PublicChatPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [state, setState] = useState('loading');
    const { theme, toggleTheme } = useTheme();

    usePrivatePageMeta(data?.title);

    useEffect(() => {
        let cancelled = false;
        setState('loading');
        fetch(`${API_BASE_URL}/api/v1/public/ai-chat/${encodeURIComponent(token || '')}`, {
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
        })
            .then(async (resp) => {
                if (!resp.ok) throw new Error(String(resp.status));
                return resp.json();
            })
            .then((payload) => {
                if (cancelled) return;
                setData(payload);
                setState('ready');
            })
            .catch(() => {
                if (!cancelled) setState('missing');
            });
        return () => { cancelled = true; };
    }, [token]);

    const authorName = data?.shared_by || data?.company || 'Пользователь';
    const sharedBy = [data?.shared_by, data?.company].filter(Boolean).join(', ');

    return (
        <div className="public-chat">
            <header className="public-chat__header">
                <a href="/" className="public-chat__brand" rel="noopener">Энилекс AI</a>
                <div className="public-chat__actions">
                    <span className="public-chat__badge">Только чтение</span>
                    <button
                        type="button"
                        className="public-chat__theme"
                        onClick={toggleTheme}
                        aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
                        title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
                    >
                        {theme === 'dark' ? <IconSun /> : <IconMoon />}
                    </button>
                </div>
            </header>

            {state === 'loading' && <div className="public-chat__state">Загружаю чат…</div>}

            {state === 'missing' && (
                <div className="public-chat__state">
                    <h2>Ссылка недействительна</h2>
                    <p>Её отозвали, срок действия истёк или адрес скопирован с ошибкой.</p>
                </div>
            )}

            {state === 'ready' && data && (
                <main className="public-chat__main">
                    <div className="public-chat__intro">
                        <h1>{data.title || 'AI-чат'}</h1>
                        <p>
                            {sharedBy ? `Поделился: ${sharedBy}` : 'Чат AI-агента Энилекс'}
                            {data.shared_at ? ` · ${formatDateTime(data.shared_at)}` : ''}
                            {data.expires_at ? ` · ссылка действует до ${formatDateTime(data.expires_at)}` : ''}
                        </p>
                    </div>
                    {data.messages.length === 0 ? (
                        <div className="public-chat__state">В чате пока нет сообщений.</div>
                    ) : (
                        <div className="public-chat__messages">
                            {data.messages.map((m, idx) => (
                                <PublicMessage key={`${m.created_at}-${idx}`} message={m} authorName={authorName} />
                            ))}
                        </div>
                    )}
                </main>
            )}
        </div>
    );
}

export default PublicChatPage;
